import { syncMasterData, syncPhotos } from './photos-db';
import type { PhotoSyncFailure, SyncProgress } from './photos-db';
import type { PhotosResponse } from '../types/photo';

export type SyncPhase = 'idle' | 'master-data' | 'photos' | 'done' | 'error';

export interface SyncState {
  phase: SyncPhase;
  progress: SyncProgress;
  failed: PhotoSyncFailure[];
  error: string | null;
}

const EMPTY_PROGRESS: SyncProgress = {
  completed: 0,
  total: 0,
  succeeded: 0,
  failed: 0
};

// Sync state lives outside React: the options dropdown unmounts SyncButton when
// it closes, and a sync in flight has to keep reporting progress regardless.
let state: SyncState = {
  phase: 'idle',
  progress: EMPTY_PROGRESS,
  failed: [],
  error: null
};

const listeners = new Set<() => void>();

function setState(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function subscribeSync(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSyncState(): SyncState {
  return state;
}

let inFlight: Promise<void> | null = null;

export function startSync(): Promise<void> {
  // Remounting the button (or double-tapping it) must not start a second sync.
  if (inFlight) return inFlight;

  inFlight = runSync().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function runSync() {
  setState({
    phase: 'master-data',
    progress: EMPTY_PROGRESS,
    failed: [],
    error: null
  });

  try {
    const token = localStorage.getItem('token');
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const masterResponse = await fetch('/api/master-data', { headers });
    if (!masterResponse.ok) {
      throw new Error(`Master data sync failed (${masterResponse.status})`);
    }
    await syncMasterData(await masterResponse.json());

    const photosResponse = await fetch('/api/photos', { headers });
    if (!photosResponse.ok) {
      throw new Error(`Photos sync failed (${photosResponse.status})`);
    }
    const photosData: PhotosResponse = await photosResponse.json();

    setState({ phase: 'photos' });

    const result = await syncPhotos(photosData.data, {
      onProgress: (progress) => setState({ progress })
    });

    setState({ phase: 'done', failed: result.failed });
  } catch (error) {
    setState({
      phase: 'error',
      error: error instanceof Error ? error.message : String(error)
    });
  }
}
